// mu — `mu pi migrate`: retire the pre-package `mu link pi` installs.
//
// pi now loads mu as a package (`pi install npm:@mu-crew/mu`). A leftover
// ~/.pi/agent/extensions/mu.ts shim would load a SECOND copy of the
// extension, so it must leave pi's discovery path first. Only files mu
// itself generated are moved, and moved aside, never deleted. Ownership is
// proven, never inferred from a name or a marker mentioned somewhere:
//   extension: a regular file byte-identical to the shim template every
//     released `mu link pi` generated; only the import URL may differ, and
//     only in its generated form (a file:// URL to dist/extension/mu-pi.js).
//   skill: a symlink resolving into THIS install's skills/mu, or a real
//     directory byte-identical to a released mu skill (KNOWN_SKILL_DIGESTS).
// Everything else is reported with a one-line note and left alone.
//
// Destinations live under `MU_PI_HOME ?? homedir()`; tests pass `home`.

import { createHash } from "node:crypto";
import { lstatSync, mkdirSync, readdirSync, readFileSync, realpathSync, renameSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, isAbsolute, join, relative, resolve, sep } from "node:path";
import { fileURLToPath } from "node:url";
import { KNOWN_SKILL_DIGESTS } from "./pi-known-skills.js";

/** First line of every shim the old `mu link pi` generated. */
export const MU_SHIM_MARKER = "// mu:shim";

export type MigrateState = "absent" | "shim" | "foreign";

export interface MigrateEntry {
  path: string;
  state: MigrateState;
  moved: boolean;
  /** Where it went (apply only). */
  to?: string;
}

export interface MigrateResult {
  extension: MigrateEntry;
  skill: MigrateEntry;
  guidance: string[];
}

export interface MigrateOptions {
  home?: string;
  /** Move what is verified mu-owned. Default: report only. */
  apply?: boolean;
  /** This install's root (holds skills/mu). Default: the package root. */
  packageRoot?: string;
  /** Digests of released skill dirs. Default: KNOWN_SKILL_DIGESTS. */
  knownSkillDigests?: readonly string[];
}

function lstatOrUndefined(path: string): ReturnType<typeof lstatSync> | undefined {
  try {
    return lstatSync(path);
  } catch {
    return undefined;
  }
}

/** A name next to `dest` that does not exist yet, so a parked file is never overwritten. */
function freeName(dest: string): string {
  if (!lstatOrUndefined(dest)) return dest;
  for (let i = 1; ; i++) {
    const candidate = `${dest}.${i}`;
    if (!lstatOrUndefined(candidate)) return candidate;
  }
}

function park(from: string, dest: string): string {
  const to = freeName(dest);
  mkdirSync(join(to, ".."), { recursive: true });
  renameSync(from, to);
  return to;
}

/** The shim every released `mu link pi` (v3.2.0 to v3.13.3) generated: one
 *  template, byte for byte, with only the import line varying. Fixture:
 *  test/fixtures/link-shim-v3.txt, produced by the old src/link.ts. */
const SHIM_HEAD = `${MU_SHIM_MARKER}
// Generated by \`mu link pi\`. Do not edit.
//
// A re-export, not a copy: the extension code lives in the mu install, so
// upgrading mu upgrades the extension. Re-run \`mu link pi\` only if the
// install path itself moves.
const { default: extension } = await import(`;
const SHIM_TAIL = `);
export default extension;
`;

/** The generated import argument: JSON.stringify(pathToFileURL(entry).href)
 *  of the built extension. Anything else (relative path, other file, a
 *  non-canonical URL) is not what mu wrote. */
function isGeneratedImport(arg: string): boolean {
  try {
    const url: unknown = JSON.parse(arg);
    return (
      typeof url === "string" &&
      JSON.stringify(url) === arg &&
      new URL(url).href === url &&
      url.startsWith("file:///") &&
      url.endsWith("/dist/extension/mu-pi.js")
    );
  } catch {
    return false;
  }
}

export function isMuShimText(text: string): boolean {
  return (
    text.startsWith(SHIM_HEAD) &&
    text.endsWith(SHIM_TAIL) &&
    isGeneratedImport(text.slice(SHIM_HEAD.length, text.length - SHIM_TAIL.length))
  );
}

function isMuShim(path: string): boolean {
  try {
    return isMuShimText(readFileSync(path, "utf8"));
  } catch {
    return false;
  }
}

/** This file's package root: src/ and the bundled dist/ both sit one level below it. */
function defaultPackageRoot(): string {
  return resolve(dirname(fileURLToPath(import.meta.url)), "..");
}

/** SHA-256 over the sorted `relative path NUL file-sha256` lines of a
 *  directory tree. undefined if it holds anything but regular files and
 *  directories (a symlink is never part of a released skill). */
export function skillDirDigest(dir: string): string | undefined {
  const lines: string[] = [];
  const walk = (d: string): boolean => {
    for (const ent of readdirSync(d, { withFileTypes: true })) {
      const p = join(d, ent.name);
      if (ent.isDirectory()) {
        if (!walk(p)) return false;
      } else if (ent.isFile()) {
        const sum = createHash("sha256").update(readFileSync(p)).digest("hex");
        lines.push(`${relative(dir, p).split(sep).join("/")}\0${sum}\n`);
      } else return false;
    }
    return true;
  };
  try {
    if (!walk(dir)) return undefined;
  } catch {
    return undefined;
  }
  return createHash("sha256").update(lines.sort().join("")).digest("hex");
}

function isMuSkill(
  path: string,
  st: NonNullable<ReturnType<typeof lstatSync>>,
  root: string,
  known: readonly string[],
): boolean {
  try {
    if (st.isSymbolicLink()) {
      const own = realpathSync(join(root, "skills", "mu"));
      const rel = relative(own, realpathSync(path));
      return rel === "" || (!rel.startsWith("..") && !isAbsolute(rel));
    }
    if (!st.isDirectory()) return false;
    const digest = skillDirDigest(path);
    return digest !== undefined && known.includes(digest);
  } catch {
    return false;
  }
}

export function migratePi(opts: MigrateOptions = {}): MigrateResult {
  const home = opts.home ?? process.env.MU_PI_HOME ?? homedir();
  const extPath = join(home, ".pi", "agent", "extensions", "mu.ts");
  const skillPath = join(home, ".agents", "skills", "mu");

  // The shim must be a regular file: a symlink (e.g. to a dev checkout) is foreign.
  const extSt = lstatOrUndefined(extPath);
  const extension: MigrateEntry = {
    path: extPath,
    state: !extSt ? "absent" : extSt.isFile() && isMuShim(extPath) ? "shim" : "foreign",
    moved: false,
  };
  const skSt = lstatOrUndefined(skillPath);
  const skill: MigrateEntry = {
    path: skillPath,
    state: !skSt
      ? "absent"
      : isMuSkill(
            skillPath,
            skSt,
            opts.packageRoot ?? defaultPackageRoot(),
            opts.knownSkillDigests ?? KNOWN_SKILL_DIGESTS,
          )
        ? "shim"
        : "foreign",
    moved: false,
  };

  if (opts.apply === true) {
    if (extension.state === "shim") {
      // `.shim`, not `.ts`: pi loads every *.ts under extensions/ and ignores the rest.
      extension.to = park(extPath, join(home, ".pi", "agent", "disabled", "mu.ts.shim"));
      extension.moved = true;
    }
    if (skill.state === "shim") {
      skill.to = park(skillPath, join(home, ".agents", "disabled", "skills", "mu"));
      skill.moved = true;
    }
  }

  const guidance: string[] = [];
  if (opts.apply !== true && (extension.state === "shim" || skill.state === "shim")) {
    guidance.push("Nothing moved. Re-run with --apply to move the mu-owned files aside.");
  }
  for (const e of [extension, skill]) {
    if (e.state === "foreign") {
      guidance.push(
        `Left ${e.path} in place: not proven mu's. If it duplicates the package, move it aside yourself.`,
      );
    }
  }
  if (extension.moved || skill.moved) {
    guidance.push(
      "Running pi agents keep the old extension until they restart: restart pi or run /reload.",
    );
  }
  guidance.push("Install the package once: pi install npm:@mu-crew/mu");
  return { extension, skill, guidance };
}
